import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@paddleverse.app");
  const [password, setPassword] = useState("Paddle123!");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back — let's get your dink together.");
      navigate("/home");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/play";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hero-glow p-8 md:p-14 flex flex-col justify-between text-white relative overflow-hidden">
        <Logo light />
        <div className="max-w-md rise-in">
          <div className="pill pv-bg-primary text-black mb-6">PICKLEBALL, SUPERCHARGED</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold leading-[1.05] mb-4">
            Let's get your <span style={{color:"#BED445"}}>dink</span> together.
          </h1>
          <p className="text-white/80 text-base sm:text-lg leading-relaxed">
            Matchmaking, live court status, ladder wars — one place for competitive players.
            No more ghost courts, no more group-chat scheduling.
          </p>
          <div className="mt-8 flex flex-wrap gap-2 text-xs font-bold">
            <span className="pill" style={{background:"rgba(190,212,69,.15)", color:"#BED445"}}>DUPR 3.5 – 5.0+</span>
            <span className="pill" style={{background:"rgba(245,183,0,.15)", color:"#F5B700"}}>Live check-ins</span>
            <span className="pill" style={{background:"rgba(74,144,226,.15)", color:"#82b7ee"}}>Ladder & clubs</span>
          </div>
        </div>
        <div className="text-xs text-white/50">Charlotte, NC · founding city</div>
      </div>

      <div className="p-6 sm:p-10 flex items-center justify-center">
        <div className="w-full max-w-md card p-6 sm:p-8 rise-in" data-testid="login-form">
          <h2 className="text-2xl font-display font-extrabold pv-text-dark">Sign in</h2>
          <p className="text-sm text-gray-600 mt-1">Use the seeded demo account or your own.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required />
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-70">
              {busy ? <Loader2 className="mx-auto animate-spin" size={18}/> : "Enter the PaddleVerse"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-gray-500">
            <div className="flex-1 h-px bg-gray-200" /> or <div className="flex-1 h-px bg-gray-200" />
          </div>

          <button data-testid="google-login-btn" onClick={googleLogin} className="w-full border-2 border-gray-200 hover:border-gray-300 rounded-full py-2.5 font-bold text-sm flex items-center justify-center gap-2 transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.4 2.6 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6.1C12.2 13.5 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/><path fill="#FBBC05" d="M10.3 28.6c-.6-1.7-.9-3.5-.9-5.4s.3-3.7.9-5.4l-7.8-6.1C.8 15.6 0 19.7 0 24s.8 8.4 2.5 12.3l7.8-7.7z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.2 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.4 0-11.8-4-13.7-9.4l-7.8 6.1C6.4 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </button>

          <p className="text-sm text-center mt-6 text-gray-600">
            New here? <Link data-testid="go-register-link" to="/register" className="font-bold pv-text-support">Create an account</Link>
          </p>

          <div className="mt-6 p-3 rounded-xl bg-[#F8F6F3] border border-gray-200 text-xs text-gray-600">
            <b>Demo login:</b> demo@paddleverse.app · Paddle123!
          </div>
        </div>
      </div>
    </div>
  );
}
